import { useCallback, useEffect, useRef, useState } from "react";import { Building2, Image as ImageIcon, Plus, RefreshCw, Star, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Loading, Pill, StateBlock } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "./ui";

const media = (url) => `${process.env.REACT_APP_BACKEND_URL}${url}`;

const PhotoRow = ({ photo, onSave, onReplace, onRemove, onToggleFeatured }) => {
  const [form, setForm] = useState({
    title: photo.title,
    caption: photo.caption,
    alt: photo.alt,
    order: photo.order,
    status: photo.status,
  });
  const fileRef = useRef(null);

  return (
    <div data-testid={`facility-photo-row-${photo.id}`} className="er-card">
      <div className="flex flex-col gap-4 sm:flex-row">
        <img
          src={media(photo.thumb_url)}
          alt={photo.alt}
          loading="lazy"
          className="h-28 w-full rounded-xl object-cover sm:w-40"
        />
        <div className="flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone={photo.status === "published" ? "success" : "warning"}>
              {photo.status === "published" ? "Publicada" : "Rascunho"}
            </Pill>
            {photo.featured ? <Pill tone="info">Destaque na página inicial</Pill> : null}
            {photo.pending_source ? <Pill tone="danger">Arquivo pendente</Pill> : null}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor={`title-${photo.id}`}>Título</Label>
              <Input
                id={`title-${photo.id}`}
                data-testid={`facility-photo-title-${photo.id}`}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor={`order-${photo.id}`}>Ordem</Label>
              <Input
                id={`order-${photo.id}`}
                type="number"
                data-testid={`facility-photo-order-${photo.id}`}
                value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label htmlFor={`caption-${photo.id}`}>Legenda</Label>
            <Textarea
              id={`caption-${photo.id}`}
              data-testid={`facility-photo-caption-${photo.id}`}
              rows={2}
              value={form.caption}
              onChange={(e) => setForm({ ...form, caption: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor={`alt-${photo.id}`}>Texto alternativo (acessibilidade)</Label>
            <Input
              id={`alt-${photo.id}`}
              data-testid={`facility-photo-alt-${photo.id}`}
              value={form.alt}
              onChange={(e) => setForm({ ...form, alt: e.target.value })}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              data-testid={`facility-photo-save-${photo.id}`}
              onClick={() => onSave(photo, { ...form, order: Number(form.order) })}
              className="rounded-full bg-sage-600 text-bone hover:bg-sage-700"
            >
              Salvar
            </Button>
            <Button
              variant="outline"
              data-testid={`facility-photo-status-${photo.id}`}
              onClick={() => onSave(photo, { status: photo.status === "published" ? "draft" : "published" })}
              className="rounded-full"
            >
              {photo.status === "published" ? "Despublicar" : "Publicar"}
            </Button>
            <Button
              variant="outline"
              data-testid={`facility-photo-featured-${photo.id}`}
              onClick={() => onToggleFeatured(photo)}
              className="rounded-full"
            >
              <Star size={14} className="mr-2" /> {photo.featured ? "Remover destaque" : "Destacar"}
            </Button>
            <Button
              variant="outline"
              data-testid={`facility-photo-replace-${photo.id}`}
              onClick={() => fileRef.current?.click()}
              className="rounded-full"
            >
              <RefreshCw size={14} className="mr-2" /> Substituir foto
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*,.heic,.heif"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && onReplace(photo, e.target.files[0])}
            />
            <Button
              variant="ghost"
              data-testid={`facility-photo-remove-${photo.id}`}
              onClick={() => onRemove(photo)}
              className="rounded-full text-danger"
            >
              <Trash2 size={14} className="mr-2" /> Remover
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const NewPhoto = ({ groupSlug, onCreate, onPrevia }) => {
  const [file, setFile] = useState(null);
  const [previaUrl, setPreviaUrl] = useState("");
  const [previaDoServidor, setPreviaDoServidor] = useState(false);
  const [previaErro, setPreviaErro] = useState("");
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [alt, setAlt] = useState("");
  const objectUrlRef = useRef("");

  // A objectURL só é revogada quando a seleção muda ou o componente sai de tela — nunca antes do load.
  useEffect(() => () => objectUrlRef.current && URL.revokeObjectURL(objectUrlRef.current), []);

  const selectFile = (picked) => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = "";
    setPreviaDoServidor(false);
    setPreviaErro("");
    setFile(picked);
    if (!picked) {
      setPreviaUrl("");
      return;
    }
    const url = URL.createObjectURL(picked);
    objectUrlRef.current = url;
    setPreviaUrl(url);
  };

  // Safari no iOS não renderiza HEIC/HEIF em objectURL: usamos a prévia normalizada pelo servidor.
  const usarPreviaDoServidor = async () => {
    if (!file || previaDoServidor) return;
    setPreviaDoServidor(true);
    const normalized = await onPrevia(file);
    if (normalized) setPreviaUrl(normalized);
    else setPreviaErro("Não conseguimos gerar a prévia desta foto. Você ainda pode enviá-la.");
  };

  const reset = () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = "";
    setFile(null);
    setPreviaUrl("");
    setPreviaDoServidor(false);
    setPreviaErro("");
    setTitle("");
    setCaption("");
    setAlt("");
  };

  return (
    <div data-testid={`facility-new-photo-${groupSlug}`} className="rounded-2xl border border-dashed border-border p-5">
      <p className="er-label">Adicionar foto</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor={`new-title-${groupSlug}`}>Título</Label>
          <Input
            id={`new-title-${groupSlug}`}
            data-testid={`facility-new-title-${groupSlug}`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor={`new-alt-${groupSlug}`}>Texto alternativo</Label>
          <Input
            id={`new-alt-${groupSlug}`}
            data-testid={`facility-new-alt-${groupSlug}`}
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
          />
        </div>
      </div>
      <div className="mt-3">
        <Label htmlFor={`new-caption-${groupSlug}`}>Legenda</Label>
        <Textarea
          id={`new-caption-${groupSlug}`}
          data-testid={`facility-new-caption-${groupSlug}`}
          rows={2}
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*,.heic,.heif"
          data-testid={`facility-new-file-${groupSlug}`}
          onChange={(e) => selectFile(e.target.files?.[0] || null)}
          className="text-sm text-ink-600"
        />
        <Button
          data-testid={`facility-new-submit-${groupSlug}`}
          disabled={!file}
          onClick={async () => {
            const ok = await onCreate(groupSlug, { file, title, caption, alt });
            if (ok) reset();
          }}
          className="rounded-full bg-sage-600 text-bone hover:bg-sage-700"
        >
          <Upload size={14} className="mr-2" /> Enviar foto
        </Button>
      </div>
      {previaUrl ? (
        <div className="mt-4">
          <img
            src={previaUrl}
            alt="Prévia da foto selecionada"
            data-testid={`facility-new-previa-${groupSlug}`}
            onError={usarPreviaDoServidor}
            className="h-32 w-auto rounded-xl border border-border object-cover"
          />
          {previaDoServidor && !previaErro ? (
            <p className="mt-2 text-xs text-ink-400">Prévia gerada pelo servidor (formato HEIC/HEIF).</p>
          ) : null}
        </div>
      ) : null}
      {previaErro ? (
        <p data-testid={`facility-new-previa-erro-${groupSlug}`} className="mt-2 text-xs text-danger">
          {previaErro}
        </p>
      ) : null}
    </div>
  );
};

export default function FacilityManager() {
  const { api } = useAuth();
  const [groups, setGroups] = useState(null);
  const [newGroup, setNewGroup] = useState({ slug: "", title: "", description: "", order: 0 });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cms/facility/groups");
      setGroups(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setGroups([]);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const savePhoto = async (photo, updates) => {
    try {
      await api.put(`/cms/facility/photos/${photo.id}`, updates);
      toast.success("Foto atualizada");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleFeatured = async (photo) => {
    const featuredCount = (groups || []).flatMap((g) => g.photos).filter((p) => p.featured).length;
    if (!photo.featured && featuredCount >= 3) {
      toast.error("A página inicial exibe três fotos. Remova um destaque antes de adicionar outro.");
      return;
    }
    await savePhoto(photo, { featured: !photo.featured });
  };

  const replacePhoto = async (photo, file) => {
    const form = new FormData();
    form.append("file", file, file.name || "foto.jpg");
    try {
      await api.post(`/cms/facility/photos/${photo.id}/replace`, form);
      toast.success("Foto substituída");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const removePhoto = async (photo) => {
    try {
      await api.delete(`/cms/facility/photos/${photo.id}`);
      toast.success("Foto removida");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const createPhoto = async (groupSlug, { file, title, caption, alt }) => {
    const form = new FormData();
    form.append("group_slug", groupSlug);
    form.append("title", title);
    form.append("caption", caption);
    form.append("alt", alt);
    form.append("status", "draft");
    form.append("file", file, file.name || "foto.jpg");
    try {
      await api.post("/cms/facility/photos", form);
      toast.success("Foto enviada como rascunho");
      load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    }
  };

  const gerarPrevia = async (file) => {
    const form = new FormData();
    form.append("file", file, file.name || "foto.jpg");
    try {
      const { data } = await api.post("/cms/facility/preview", form);
      return data.data_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return null;
    }
  };

  const createGroup = async () => {
    try {
      await api.post("/cms/facility/groups", { ...newGroup, order: Number(newGroup.order) });
      toast.success("Grupo criado");
      setNewGroup({ slug: "", title: "", description: "", order: 0 });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (groups === null) return <Loading label="Carregando estrutura…" />;

  return (
    <div data-testid="facility-manager" className="space-y-10">
      <PageHeader
        kicker="Conteúdo público"
        title="Estrutura"
        subtitle="Grupos, fotos, legendas e textos alternativos exibidos na aba Estrutura do aplicativo Erimus – Saúde Mental. Até três fotos publicadas podem ser destacadas na página inicial."
      />

      {groups.length === 0 ? (
        <StateBlock
          testId="facility-manager-empty"
          icon={Building2}
          title="Nenhum grupo cadastrado"
          description="Crie um grupo para começar a organizar as fotos da estrutura."
        />
      ) : null}

      {groups.map((group) => (
        <section key={group.id} data-testid={`facility-group-manager-${group.slug}`} className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-medium text-ink-900">{group.title}</h2>
              <p className="mt-1 max-w-2xl text-sm text-ink-500">{group.description}</p>
            </div>
            <Pill tone="info">{group.photos.length} foto(s)</Pill>
          </div>

          {group.photos.length === 0 ? (
            <p className="flex items-center gap-2 rounded-2xl bg-sand px-4 py-3 text-sm text-ink-500">
              <ImageIcon size={15} /> Nenhuma foto neste grupo.
            </p>
          ) : (
            group.photos.map((photo) => (
              <PhotoRow
                key={photo.id}
                photo={photo}
                onSave={savePhoto}
                onReplace={replacePhoto}
                onRemove={removePhoto}
                onToggleFeatured={toggleFeatured}
              />
            ))
          )}

          <NewPhoto groupSlug={group.slug} onCreate={createPhoto} onPrevia={gerarPrevia} />
        </section>
      ))}

      <section data-testid="facility-new-group" className="er-card">
        <p className="er-label">Novo grupo</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="group-title">Título</Label>
            <Input
              id="group-title"
              data-testid="facility-group-title"
              value={newGroup.title}
              onChange={(e) => setNewGroup({ ...newGroup, title: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="group-slug">Identificador</Label>
            <Input
              id="group-slug"
              data-testid="facility-group-slug"
              value={newGroup.slug}
              onChange={(e) => setNewGroup({ ...newGroup, slug: e.target.value })}
            />
          </div>
        </div>
        <div className="mt-3">
          <Label htmlFor="group-description">Descrição</Label>
          <Textarea
            id="group-description"
            data-testid="facility-group-description"
            rows={2}
            value={newGroup.description}
            onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
          />
        </div>
        <Button
          data-testid="facility-group-create"
          onClick={createGroup}
          disabled={!newGroup.slug || !newGroup.title}
          className="mt-4 rounded-full bg-sage-600 text-bone hover:bg-sage-700"
        >
          <Plus size={15} className="mr-2" /> Criar grupo
        </Button>
      </section>
    </div>
  );
}
